import React, { useContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  ActionIcon,
  Anchor,
  Checkbox,
  Divider,
  List,
  Loader,
  Radio,
  TextInput
} from '@mantine/core';
import { ChevronDown, ChevronUp } from 'tabler-icons-react';
import { Link } from 'react-router-dom';
import ResponsiveModal from '../../common/ResponsiveModal';
import AppStack from '../../common/AppStack';
import AppText from '../../common/AppText';
import { singularPluralFormat } from '../../../helpers/format';
import { Context as AuthContext } from '../../../providers/AuthContextProvider';
import { Context as VendorContext } from '../../../providers/VendorContextProvider';
import { triggerNotification } from '../../../helpers/notification';
import PaginationList from '../../common/PaginationList';
import AppFlexbox from '../../common/AppFlexbox';

const RemoveVendorProductVariantsModal = ({
  isOpen,
  onClose,
  selectedProductIds,
  filter,
  onSuccess
}) => {
  const { state: authState } = useContext(AuthContext);
  const {
    state,
    fetchVendorProductsVariantOptions,
    removeVendorProductsVariantOptions
  } = useContext(VendorContext);
  const [formState, setFormState] = useState({
    pageIndex: 1,
    search: '',
    colorValues: [],
    expandedValues: [],
    removeProductSync: true,
    selectAll: false,
    showProductSelect: false,
    showConfirm: false,
    viewAffectedProducts: false,
    loading: false
  });
  const hasFilter =
    filter &&
    (!!filter.fkEcomProductStatus ||
      !!filter.fkEcomCategory ||
      !!filter.fkEcomExternalConnectionType ||
      !!filter.fkCollection ||
      !!filter.fkEcomVendorDesign ||
      !!filter.search);

  const {
    loading,
    value: productsWithVariantOptions,
    filter: currentFilter
  } = state.ecomVendorProductsVariantOptions;

  const variantOptions = productsWithVariantOptions?.ecomVendorProducts.reduce(
    (r, c) => {
      c.variantOptions.forEach((op) => {
        let option = r.find((f) => f.name === op.name.toLowerCase());
        if (!option) {
          option = { name: op.name.toLowerCase(), values: [] };
          r.push(option);
        }

        op.values.forEach((v) => {
          if (option.values.every((f) => f !== v.toLowerCase())) {
            option.values.push(v.toLowerCase());
          }
        });
      });

      return r;
    },
    []
  );
  const colorVariantOption = variantOptions?.find((o) => o.name === 'color');
  const productsSelected = productsWithVariantOptions?.ecomVendorProducts.filter(
    (p) =>
      p.variantOptions.some(
        (o) =>
          o.name.toLowerCase() === 'color' &&
          o.values.some((val) =>
            formState.colorValues.includes(val.toLowerCase())
          )
      )
  );

  useEffect(() => {
    if (isOpen) {
      setFormState({
        pageIndex: 1,
        search: '',
        colorValues: [],
        expandedValues: [],
        removeProductSync: true,
        selectAll: false,
        showProductSelect: selectedProductIds?.length > 0,
        showConfirm: false,
        viewAffectedProducts: false,
        loading: false
      });

      if (!selectedProductIds || selectedProductIds.length === 0) {
        // eslint-disable-next-line no-use-before-define
        fetchProductInfo(true);
      }
    }
  }, [isOpen]);

  const fetchProductInfo = (allProducts = false) => {
    fetchVendorProductsVariantOptions(
      authState.pkEcomVendor,
      allProducts ? filter || {} : { uuids: selectedProductIds },
      () => {},
      (e) => {
        triggerNotification(e);
      }
    );
  };

  const onSubmit = () => {
    setFormState({ ...formState, loading: true });
    removeVendorProductsVariantOptions(
      authState.pkEcomVendor,
      {
        removeProductSync: formState.removeProductSync,
        filter:
          formState.selectAll ||
          !selectedProductIds ||
          selectedProductIds.length === 0
            ? {
                search: filter?.search,
                fkEcomProductStatus: filter?.fkEcomProductStatus,
                fkEcomCategory: filter?.fkEcomCategory,
                fkEcomExternalConnectionType:
                  filter?.fkEcomExternalConnectionType,
                fkCollection:
                  filter?.fkEcomVendorCollection || filter?.fkCollection,
                fkDesign: filter?.fkEcomVendorDesign || filter?.fkDesign,
                uuids: filter?.uuids
              }
            : { uuids: selectedProductIds },
        colorValues: formState.colorValues
      },
      () => {
        triggerNotification('Product variants removed', 'success');
        onClose();
        if (onSuccess) {
          onSuccess();
        }
      },
      (e) => {
        triggerNotification(e);
        setFormState({ ...formState, loading: false });
      }
    );
  };

  return (
    <ResponsiveModal
      formSectionProps={
        formState.showConfirm
          ? {
              isLoading: formState.loading,
              isSubmitDisabled: false,
              onCancel: () => {
                setFormState({ ...formState, showConfirm: false, search: '' });
              },
              onSubmit,
              submitTitle: 'Yes, remove variants',
              cancelTitle: 'Back'
            }
          : formState.showProductSelect
          ? {
              onCancel: onClose,
              onSubmit: () => {
                setFormState({ ...formState, showProductSelect: false });
                fetchProductInfo(formState.selectAll);
              },
              submitTitle: 'Continue'
            }
          : {
              isSubmitDisabled: formState.colorValues.length === 0,
              onCancel: onClose,
              onSubmit: () => {
                setFormState({ ...formState, showConfirm: true, search: '' });
              },
              submitTitle: 'Continue'
            }
      }
      isOpen={isOpen}
      onClose={onClose}
      title="Remove product variants?"
    >
      {formState.showConfirm ? (
        <AppStack>
          <AppStack style={{ gap: 8 }}>
            <AppText style={{ fontSize: 14 }}>
              Are you sure you want to remove the following{' '}
              {formState.colorValues.length === 1 ? 'color' : 'colors'}?
            </AppText>

            <List withPadding>
              {formState.colorValues.map((v) => (
                <List.Item key={v}>
                  <AppText style={{ fontSize: 14, fontWeight: 500 }}>
                    {v.charAt(0).toUpperCase() + v.slice(1)}
                  </AppText>
                </List.Item>
              ))}
            </List>
          </AppStack>

          <AppText style={{ fontSize: 14 }}>
            This will update{' '}
            <span style={{ fontWeight: 500 }}>
              {singularPluralFormat(
                productsSelected?.length ?? 0,
                'product',
                'products'
              )}
            </span>{' '}
            to remove the variants matching any selected color.
          </AppText>

          <AppText style={{ fontSize: 14, fontWeight: 500 }}>
            This action cannot be undone.
          </AppText>

          <Divider />

          <Checkbox
            checked={formState.removeProductSync}
            description="If selected, all products will stop syncing new variants to prevent removed colors from coming back. You can re-enable this later in the product settings."
            disabled={formState.loading}
            label="Disable new variant sync"
            onChange={() =>
              setFormState({
                ...formState,
                removeProductSync: !formState.removeProductSync
              })
            }
            style={{ marginTop: 10 }}
            styles={{ label: { fontWeight: 500 } }}
          />

          <AppFlexbox>
            <Anchor
              onClick={() =>
                setFormState({
                  ...formState,
                  viewAffectedProducts: !formState.viewAffectedProducts
                })
              }
              style={{ fontSize: 14 }}
            >
              {formState.viewAffectedProducts
                ? 'Hide products'
                : 'View products'}
            </Anchor>
          </AppFlexbox>

          {formState.viewAffectedProducts && (
            <AppStack style={{ gap: 5 }}>
              <AppText style={{ fontSize: 14, fontWeight: 500 }}>
                Affected products ({productsSelected?.length ?? 0})
              </AppText>
              <TextInput
                onChange={(e) => {
                  setFormState({
                    ...formState,
                    search: e.currentTarget.value,
                    pageIndex: 1
                  });
                }}
                placeholder="Search by name..."
                value={formState.search}
              />
              <PaginationList
                emptyMessage="No products available"
                items={
                  productsSelected
                    .sort((a, b) => a.name.localeCompare(b.name))
                    .filter(
                      (p) =>
                        !formState.search ||
                        p.name
                          .toLowerCase()
                          .includes(formState.search.toLowerCase().trim())
                    )
                    .map((p) => (
                      <AppFlexbox
                        key={p.uuid}
                        style={{
                          padding: 8,
                          alignItems: 'center',
                          gap: 8,
                          flex: 1
                        }}
                      >
                        <Anchor
                          component={Link}
                          style={{ fontSize: 14 }}
                          target="_blank"
                          to={`/vendor/products/${p.uuid}`}
                        >
                          {p.name}
                        </Anchor>
                      </AppFlexbox>
                    )) || []
                }
                itemsPerPage={10}
              />
            </AppStack>
          )}
        </AppStack>
      ) : formState.showProductSelect ? (
        <AppStack>
          <Radio.Group
            description="Select the products to remove variants from."
            label="Remove variants from"
            onChange={(v) => {
              setFormState({
                ...formState,
                selectAll: v === 'all'
              });
            }}
            value={formState.selectAll ? 'all' : 'selected'}
          >
            <AppStack style={{ gap: 10, marginTop: 10 }}>
              <Radio
                label={hasFilter ? 'All products by filter' : 'All products'}
                value="all"
              />
              <Radio
                label={`Selected ${singularPluralFormat(
                  selectedProductIds?.length ?? 0,
                  'product',
                  'products'
                )}`}
                value="selected"
              />
            </AppStack>
          </Radio.Group>
        </AppStack>
      ) : (
        <AppStack>
          {loading ? (
            <AppStack
              style={{
                padding: 40,
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <Loader color="dark" size={40} />
            </AppStack>
          ) : (
            <AppStack style={{ gap: 16 }}>
              <AppText
                style={{
                  fontSize: 14,
                  color: '#999',
                  textAlign: 'center',
                  fontStyle: 'italic'
                }}
              >
                Select the variants you want to remove.
              </AppText>

              <AppStack style={{ gap: 5 }}>
                <AppStack style={{ gap: 5 }}>
                  <AppText style={{ fontSize: 14, fontWeight: 500 }}>
                    Colors
                  </AppText>

                  <TextInput
                    onChange={(e) => {
                      setFormState({
                        ...formState,
                        search: e.currentTarget.value,
                        pageIndex: 1
                      });
                    }}
                    placeholder="Search by color..."
                    value={formState.search}
                  />

                  <PaginationList
                    emptyMessage="No colors available"
                    isLoading={loading}
                    items={
                      colorVariantOption?.values
                        .filter(
                          (v) =>
                            !formState.search ||
                            v.includes(formState.search.toLowerCase().trim())
                        )
                        ?.sort((a, b) => a.localeCompare(b))
                        .map((v) => {
                          const selected = formState.colorValues.includes(v);
                          const isExpanded = formState.expandedValues.includes(
                            v
                          );

                          return (
                            <AppStack key={v} style={{ gap: 0, flex: 1 }}>
                              <AppFlexbox
                                onClick={() => {
                                  setFormState({
                                    ...formState,
                                    colorValues: selected
                                      ? formState.colorValues.filter(
                                          (f) => f !== v
                                        )
                                      : [...formState.colorValues, v]
                                  });
                                }}
                                style={{
                                  padding: 8,
                                  alignItems: 'center',
                                  justifyContent: 'space-between',
                                  gap: 8,
                                  flex: 1,
                                  cursor: formState.loading
                                    ? 'not-allowed'
                                    : 'pointer'
                                }}
                              >
                                <AppFlexbox
                                  style={{
                                    alignItems: 'center',
                                    gap: 8,
                                    flex: 1
                                  }}
                                >
                                  <Checkbox
                                    checked={selected}
                                    disabled={formState.loading}
                                    onChange={() => {}}
                                  />
                                  <AppText
                                    style={{ fontSize: 14, fontWeight: 500 }}
                                  >
                                    {v.charAt(0).toUpperCase() + v.slice(1)}
                                  </AppText>
                                </AppFlexbox>
                                <ActionIcon
                                  color="dark"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setFormState({
                                      ...formState,
                                      expandedValues: formState.expandedValues.includes(
                                        v
                                      )
                                        ? formState.expandedValues.filter(
                                            (f) => f !== v
                                          )
                                        : [...formState.expandedValues, v]
                                    });
                                  }}
                                  size="md"
                                  variant="subtle"
                                >
                                  {isExpanded ? (
                                    <ChevronUp size={22} />
                                  ) : (
                                    <ChevronDown size={22} />
                                  )}
                                </ActionIcon>
                              </AppFlexbox>
                              {isExpanded && (
                                <>
                                  <Divider />
                                  <AppStack style={{ gap: 5, padding: 8 }}>
                                    {productsWithVariantOptions?.ecomVendorProducts
                                      .filter((p) =>
                                        p.variantOptions.some(
                                          (o) =>
                                            o.name.toLowerCase() === 'color' &&
                                            o.values.some(
                                              (val) => val.toLowerCase() === v
                                            )
                                        )
                                      )
                                      .sort((a, b) =>
                                        a.name.localeCompare(b.name)
                                      )
                                      .map((p) => (
                                        <Anchor
                                          key={p.uuid}
                                          component={Link}
                                          style={{ fontSize: 13 }}
                                          target="_blank"
                                          to={`/vendor/products/${p.uuid}`}
                                        >
                                          {p.name}
                                        </Anchor>
                                      ))}
                                  </AppStack>
                                </>
                              )}
                            </AppStack>
                          );
                        }) || []
                    }
                    itemsPerPage={10}
                    onPageChange={(pageIndex) =>
                      setFormState({
                        ...formState,
                        pageIndex
                      })
                    }
                    pageIndex={formState.pageIndex}
                  />
                </AppStack>
              </AppStack>
            </AppStack>
          )}
        </AppStack>
      )}
    </ResponsiveModal>
  );
};

RemoveVendorProductVariantsModal.propTypes = {
  fetchProductInfo: PropTypes.func,
  filter: PropTypes.object,
  isOpen: PropTypes.bool,
  onClose: PropTypes.func,
  onSuccess: PropTypes.func,
  selectedProductIds: PropTypes.array
};

export default RemoveVendorProductVariantsModal;
